/**
 * paperColumn — 지면을 **열로 세울지 서랍에 넣을지** 정하는 자(오너 2026-09-15 「화면이 충분히 넓으면 지면이 숨지 않고 펼쳐지게」).
 *
 * 두 창이 쓴다 — 시험지 편집(PaperComposeModal · 시험지 v2 와 학습 메뉴가 같은 부품)과 학습 메뉴 「테스트 생성」(HubPaperPickerModal).
 * 두 창은 이제 1400px 상한 없이 화면 폭의 98% 를 쓴다(창에 !max-w-none). 그래서 판정은 화면 폭(미디어 쿼리)이 아니라
 * **창 안 열 상자의 실제 폭**으로 한다 — 강의 칸처럼 창 안에서 폭을 먹는 것이 생겨도 같은 자로 잰다.
 * 열 상자 폭 = 창 폭 − 32 다(몸통 좌우 여백 16씩 — 2026-09-15 실측: 창 1254.4 → 열 상자 1222.4).
 *
 * 각 보기의 목록 영역에 필요한 폭을 남기고, 지면의 최소 폭이 확보되면 열로 표시한다.
 */
import { useLayoutEffect, useState } from 'react';

// ── 시험지 편집 ──────────────────────────────────────────────────────────
// 강의 칸 폭 — SyllabusPickOverlay 의 w-80(20rem). 그쪽 폭을 바꾸면 여기도 바꾼다
export const SYLLABUS_PANEL_PX = 320;
// 세 열이 서던 열 상자 폭 = 화면 xl(1280) 에서의 열 상자 폭(1280 × 0.98 − 32 = 1222.4). 머리 설명(PaperComposeModal)의
// 「경계를 xl 에 둔 근거」가 이 폭이다. 강의 칸을 편 동안은 그 320px 을 빼고 같은 자로 잰다
export const COMPOSE_THREE_COL_ROW_PX = 1222;
// 세 열일 때 검색·담은 문제 열이 **강의 칸을 뺀 폭**에서 갖는 몫. 강의 칸을 접으면 종전 w-[32%]·w-[30%] 와 같다
export const COMPOSE_SEARCH_SHARE = 0.32;
export const COMPOSE_ITEMS_SHARE = 0.30;

/** 시험지 편집 — 강의 칸을 뺀 열 상자 폭이 세 열이 서던 폭 이상이면 지면이 열로 선다 */
export function composePaperAsColumn(rowPx, sylOpen) {
  return rowPx - (sylOpen ? SYLLABUS_PANEL_PX : 0) >= COMPOSE_THREE_COL_ROW_PX;
}

/** 시험지 편집 — 세 열일 때 한 열의 폭(CSS). 강의 칸이 가져간 폭을 뺀 나머지의 몫이다 */
export function composeColumnWidth(share, sylOpen) {
  return `calc((100% - ${sylOpen ? SYLLABUS_PANEL_PX : 0}px) * ${share})`;
}

// ── 테스트 생성 ──────────────────────────────────────────────────────────
// 지면 열의 바닥 — 시험지 편집이 세 열로 바뀌는 순간 지면 열이 받는 폭(1222 × 0.38 − 강의 손잡이 28 = 436.4).
// 이보다 좁은 지면 열은 어느 창에서도 세우지 않는다
export const PAPER_COLUMN_MIN_PX = 436;
// 목록·상세 두 칸이 지금(창 1400 상한) 차지하던 폭 = 1400 − 몸통 여백 32 − 손잡이 띠 pr-9(36). 지면 열이 서도 이 폭 아래로 줄지 않는다
export const PICKER_LISTS_PX = 1332;
// 오답노트는 시험 카드 320 + 문제 영역 560 + 간격·좌우 여백 40 을 남긴다.
// 문제 행(509px)은 줄이지 않고 부모 영역의 빈 여백을 줄인다. 화면 약 1440px부터 지면이 옆에 선다.
export const PICKER_WRONG_NOTE_LISTS_PX = 920;
// 지면 열 겉껍질이 지면 칸 밖에 두르는 폭 — 오른쪽 여백 pr-4(16) + 카드 테두리 좌우(2)
export const PICKER_COLUMN_EDGE_PX = 18;

/** 테스트 생성 — 보기별 목록 폭을 남기고 지면의 최소 폭이 확보되면 우측 칸에 표시한다. */
export function pickerPaperAsColumn(rowPx, listsPx = PICKER_LISTS_PX) {
  return rowPx - listsPx >= PAPER_COLUMN_MIN_PX + PICKER_COLUMN_EDGE_PX;
}

/**
 * 테스트 생성 — 지면 열 겉껍질 폭(px). 남는 폭을 지면이 먼저 갖되 **서랍 폭(지면 100%)까지만** 갖는다.
 * 그 넘는 폭은 목록·상세가 갖는다 — 지면을 실물보다 크게 키우는 것보다 표를 넓히는 편이 쓸모 있다.
 */
export function pickerPaperColumnPx(rowPx, drawerPx, listsPx = PICKER_LISTS_PX) {
  return Math.min(drawerPx + PICKER_COLUMN_EDGE_PX, rowPx - listsPx);
}

/**
 * 요소의 겉 폭(offsetWidth, 테두리 포함 · 정수)을 잰다. [콜백 ref, 폭] 을 돌려준다.
 * 겉 폭을 쓰는 이유: 서랍 손잡이 띠(pr-9)는 판정에 따라 붙었다 떨어진다 — 안쪽 폭을 재면 그 36px 이 판정을 되흔들어
 * 경계에서 열↔서랍을 오간다. 콜백 ref 인 이유는 usePaperSheet 의 boxRef 와 같다(창이 알맹이를 늦게 붙여도 놓치지 않게).
 * 첫 페인트 전에 재려고 layout effect 를 쓴다 — 넓은 화면에서 서랍이 한 프레임 스쳤다 열로 바뀌지 않게.
 * ResizeObserver 가 없는 곳(jsdom)에서는 한 번만 잰다(0 이면 서랍 — 종전 동작 그대로).
 */
export function useBoxWidth() {
  const [el, setEl] = useState(null);
  const [width, setWidth] = useState(0);
  useLayoutEffect(() => {
    if (el == null) return undefined;
    const read = () => setWidth(el.offsetWidth);
    read();
    if (typeof ResizeObserver === 'undefined') return undefined;
    const ro = new ResizeObserver(read);
    ro.observe(el);
    return () => ro.disconnect();
  }, [el]);
  return [setEl, width];
}
